import {createReducer, on} from '@ngrx/store';
import {
  downloadCadModelRevisionDrawing,
  downloadCadModelRevisionDrawingFailure,
  downloadCadModelRevisionDrawingSuccess,
  loadCadModelRevisionDrawings,
  loadCadModelRevisionDrawingsFailure,
  loadCadModelRevisionDrawingsSuccess,
  loadCadModelDrawingBlob,
  loadCadModelDrawingBlobFailure,
  loadCadModelDrawingBlobSuccess,
  uploadCadModelRevisionDrawing, uploadCadModelRevisionDrawingFailure,
  uploadCadModelRevisionDrawingSuccess,
  toggleDrawingRedline,
  toggleDrawingRedlineSuccess,
  toggleDrawingNonConformance,
  toggleDrawingNonConformanceSuccess,
  loadBuildKitDrawings,
  loadBuildKitDrawingsSuccess,
  loadBuildKitDrawingsFailure,
  saveDrawingScribbleSuccess,
} from "./cad-model-revision-drawing.action";
import {
  CadModelRevisionDrawing, Drawing
} from "../../../shared/models/cad-model.model";
import {StateStatus} from "../../../shared/models/state-status.models";
import {createEntityAdapter, EntityState} from "@ngrx/entity";

const cadModelRevisionDrawingAdapter = createEntityAdapter<CadModelRevisionDrawing>()

export interface CadModelRevisionDrawingState extends EntityState<CadModelRevisionDrawing>{
  selectedCadModelRevisionId: string;
  revisionDrawingsStatus: StateStatus;
  revisionDrawingUploadStatus: StateStatus;
  downloadDrawingStatus: StateStatus;
  cadModelDrawingBlobMap: any;
  cadModelDrawingsStatus: StateStatus;
  buildKitDrawingsMap: Record<string, Drawing[]>;
  buildKitDrawingsStatus: StateStatus;
  error: any;
}

export const initialState: CadModelRevisionDrawingState = cadModelRevisionDrawingAdapter.getInitialState({
  selectedCadModelRevisionId: '',
  revisionDrawingsStatus: 'pending',
  revisionDrawingUploadStatus: 'pending',
  downloadDrawingStatus: 'pending',
  cadModelDrawingBlobMap: {},
  cadModelDrawingsStatus: 'pending',
  buildKitDrawingsMap: {},
  buildKitDrawingsStatus: 'pending' as StateStatus,
  error: null
});

export const cadModelRevisionDrawingReducer = createReducer(
  initialState,
  on(loadCadModelRevisionDrawings, (state, { cadModelRevisionId}): CadModelRevisionDrawingState => ({
    ...state,
    revisionDrawingsStatus: 'loading' as StateStatus,
    selectedCadModelRevisionId: cadModelRevisionId
  })),
  on(loadCadModelRevisionDrawingsSuccess, (state, { cadModelRevisionDrawing }): CadModelRevisionDrawingState => {
    return cadModelRevisionDrawingAdapter.upsertOne({
        id: cadModelRevisionDrawing.id,
        drawings: cadModelRevisionDrawing.drawings
      }, {
        ...state,
        revisionDrawingsStatus: 'loadSuccess' as StateStatus
      })
    }
  ),
  on(loadCadModelRevisionDrawingsFailure, (state, { error }): CadModelRevisionDrawingState => ({
    ...state,
    error: error,
    revisionDrawingsStatus: 'error' as StateStatus,
  })),
  on(downloadCadModelRevisionDrawing, (state): CadModelRevisionDrawingState => ({
    ...state,
    downloadDrawingStatus: 'loading' as StateStatus,
  })),
  on(downloadCadModelRevisionDrawingSuccess, (state, { file }): CadModelRevisionDrawingState => ({
    ...state,
    downloadDrawingStatus: 'loadSuccess' as StateStatus,
  })),
  on(downloadCadModelRevisionDrawingFailure, (state, { error }): CadModelRevisionDrawingState => ({
    ...state,
    error: error,
    downloadDrawingStatus: 'loadError' as StateStatus,
  })),
  on(uploadCadModelRevisionDrawing, (state): CadModelRevisionDrawingState => ({
    ...state,
    revisionDrawingUploadStatus: 'uploading' as StateStatus,
  })),
  on(uploadCadModelRevisionDrawingSuccess, (state): CadModelRevisionDrawingState => ({
    ...state,
    revisionDrawingUploadStatus: 'uploadSuccess' as StateStatus,
  })),
  on(uploadCadModelRevisionDrawingFailure, (state, { error }): CadModelRevisionDrawingState => ({
    ...state,
    error: error,
    revisionDrawingUploadStatus: 'uploadError' as StateStatus,
  })),
  on(loadCadModelDrawingBlob, (state, { pdfDrawingFileKey }) => ({
    ...state,
    cadModelDrawingBlobMap: {
      ...state.cadModelDrawingBlobMap,
      [pdfDrawingFileKey]: cadModelDrawingBlobValues(pdfDrawingFileKey, state.cadModelDrawingBlobMap),
    }
  })),
  on(loadCadModelDrawingBlobSuccess, (state, { pdfDrawingFileKey, blob }) => ({
    ...state,
    cadModelDrawingBlobMap: {
      ...state.cadModelDrawingBlobMap,
      [pdfDrawingFileKey]: {
        blob,
        completed: true,
        inProgress: false,
        previousCallInProgress: false
      }
    }
  })),
  on(loadCadModelDrawingBlobFailure, (state, { error }) => ({
    ...state,
    error
  })),
  on(saveDrawingScribbleSuccess, (state, { pdfDrawingFileKey, blob }) => ({
    ...state,
    cadModelDrawingBlobMap: {
      ...state.cadModelDrawingBlobMap,
      [pdfDrawingFileKey]: {
        blob,
        completed: true,
        inProgress: false,
        previousCallInProgress: false,
      },
    },
  })),
  on(toggleDrawingRedline, (state, { drawingId }): CadModelRevisionDrawingState => {
    const newEntities: any = { ...state.entities };
    for (const key in newEntities) {
      const entity = newEntities[key];
      if (entity && entity.drawings.some((d: any) => d.id === drawingId)) {
        newEntities[key] = { ...entity, drawings: entity.drawings.map((d: any) => d.id === drawingId ? { ...d, isRedline: !d.isRedline } : d) };
        break;
      }
    }
    const newBuildKitDrawingsMap: Record<string, Drawing[]> = {};
    for (const kitId in state.buildKitDrawingsMap) {
      newBuildKitDrawingsMap[kitId] = state.buildKitDrawingsMap[kitId].map(
        (d: Drawing) => d.id === drawingId ? { ...d, isRedline: !d.isRedline } : d
      );
    }
    return { ...state, entities: newEntities, buildKitDrawingsMap: newBuildKitDrawingsMap };
  }),
  on(toggleDrawingNonConformance, (state, { drawingId }): CadModelRevisionDrawingState => {
    const newEntities: any = { ...state.entities };
    for (const key in newEntities) {
      const entity = newEntities[key];
      if (entity && entity.drawings.some((d: any) => d.id === drawingId)) {
        newEntities[key] = { ...entity, drawings: entity.drawings.map((d: any) => d.id === drawingId ? { ...d, isNonConformance: !d.isNonConformance } : d) };
        break;
      }
    }
    const newBuildKitDrawingsMap: Record<string, Drawing[]> = {};
    for (const kitId in state.buildKitDrawingsMap) {
      newBuildKitDrawingsMap[kitId] = state.buildKitDrawingsMap[kitId].map(
        (d: Drawing) => d.id === drawingId ? { ...d, isNonConformance: !d.isNonConformance } : d
      );
    }
    return { ...state, entities: newEntities, buildKitDrawingsMap: newBuildKitDrawingsMap };
  }),
  on(toggleDrawingRedlineSuccess, toggleDrawingNonConformanceSuccess, (state, { drawing }): CadModelRevisionDrawingState => {
    const newEntities: any = { ...state.entities };
    for (const key in newEntities) {
      const entity = newEntities[key];
      if (entity && entity.drawings.some((d: any) => d.id === drawing.id)) {
        newEntities[key] = {
          ...entity,
          drawings: entity.drawings.map((d: any) => d.id === drawing.id ? drawing : d),
        };
        break;
      }
    }
    const newBuildKitDrawingsMap: Record<string, Drawing[]> = {};
    for (const kitId in state.buildKitDrawingsMap) {
      newBuildKitDrawingsMap[kitId] = state.buildKitDrawingsMap[kitId].map(
        (d: Drawing) => d.id === drawing.id ? drawing : d
      );
    }
    return { ...state, entities: newEntities, buildKitDrawingsMap: newBuildKitDrawingsMap };
  }),
  on(loadBuildKitDrawings, (state): CadModelRevisionDrawingState => ({
    ...state,
    buildKitDrawingsStatus: 'loading' as StateStatus,
  })),
  on(loadBuildKitDrawingsSuccess, (state, { buildKitId, drawings }): CadModelRevisionDrawingState => ({
    ...state,
    buildKitDrawingsStatus: 'loadSuccess' as StateStatus,
    buildKitDrawingsMap: { ...state.buildKitDrawingsMap, [buildKitId]: drawings },
  })),
  on(loadBuildKitDrawingsFailure, (state, { error }): CadModelRevisionDrawingState => ({
    ...state,
    buildKitDrawingsStatus: 'error' as StateStatus,
    error,
  })),
);
function cadModelDrawingBlobValues(pdfDrawingFileKey: string, commonDrawingBlobMap: any){
  if(pdfDrawingFileKey in commonDrawingBlobMap){
    return {
      ...commonDrawingBlobMap[pdfDrawingFileKey],
      previousCallInProgress: commonDrawingBlobMap[pdfDrawingFileKey].inProgress
    };
  } else {
    return {
      blob: undefined,
      inProgress: true,
      previousCallInProgress: false,
      completed: false,
    }
  }
}
